import React, { FunctionComponent, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStore, useStoreMap } from 'effector-react';
import { Spin, Image } from 'antd';
import moment from 'moment';
import cn from 'classnames';
import { CalendarOutlined } from '@ant-design/icons';
import UserShortCard from '@components/UserShortCard';
import { FULL_TIME_FORMAT } from '@config/format';
import IComment from '@interfaces/IComment';
import { getPath, goTo } from '@helpers/navigation';
import { IImage } from '@interfaces/IImage';
import useModal from '@hooks/useModal';
import SpamPanel from '@components/SpamPanel';
import comments, { block, getComments, unblock } from '@stores/comments';
import ImageModal from '@components/FullImageModal/FullImageModal';
import { FileType } from '@interfaces/MediaFile';
import componentContent from '@components/Comment/Comment.content.json';
import css from './SpamCommentCard.module.less';
import useTranslate from '@hooks/useTranslate';
import { getList } from '@stores/spamComments';

interface SpamCommentCardProps {
  comment: IComment;
  isSubComment?: boolean;
}

const SpamCommentCard: FunctionComponent<SpamCommentCardProps> = (props) => {
  const [currentImage, setCurrentImage] = useState<IImage | null>(null);
  const { comment, isSubComment } = props;
  const { store, loading } = useStoreMap({
    store: comments,
    keys: [comment.id],
    fn: (comments, [commentId]) => {
      return {
        store: comments.list[commentId] || null,
        loading: comments.preloaders.getComments === commentId || false,
      };
    },
  });

  const { preloaders } = useStore(comments);
  const [isVisible, openModal, closeModal] = useModal();

  const content = useTranslate(componentContent);

  const getCommentGifUrl = (comment: IComment): string | null => {
    if (typeof comment.gifUrl === 'string' && comment.gifUrl) return comment.gifUrl;

    const contentGif = Array.isArray(comment.content)
      ? comment.content.find((c) => typeof c?.gifUrl === 'string' && c.gifUrl)?.gifUrl
      : undefined;

    if (contentGif) return contentGif;

    return null;
  };

  const renderCommentText = (comment: IComment) => {
    const gifUrl = getCommentGifUrl(comment);

    if (gifUrl) {
      return (
        <>
          <a href={gifUrl}>{gifUrl}</a>
          <br />
          <Image
            src={gifUrl}
            width={120}
          />
        </>
      );
    }

    return comment.text;
  };

  const isBlocking = preloaders.block === `block_${comment.id}`;
  const isUnblocking = preloaders.unblock === `unblock_${comment.id}`;

  const goToUserDetail = () => goTo('userDetail', { userId: comment.author.id });

  const remainingComments = store
    ? store.pagination.total - (store.pagination.current + 1) * store.pagination.pageSize
    : comment.childrenComments;

  const isDisplayedSpamPanel = !isSubComment;
  const isDisplayedRemainingCommentsButton =
    !isSubComment && remainingComments && remainingComments > 0;
  const isLoading = !isSubComment && loading;

  const openModalHandler = useCallback(
    (image) => {
      setCurrentImage(image);
      openModal();
    },
    [openModal],
  );

  const blockComment = useCallback(() => {
    !isBlocking &&
      block({
        commentId: comment.id,
        preloaderId: `block_${comment.id}`,
      }).then(() => getList());
  }, [isBlocking, comment.id]);

  const unblockComment = useCallback(() => {
    !isUnblocking &&
      unblock({
        commentId: comment.id,
        preloaderId: `unblock_${comment.id}`,
      }).then(() => getList());
  }, [isUnblocking, comment.id]);

  return (
    <>
      <div className={cn(css.root, { [css.rootBlocked]: comment.isBlocked })}>
        <Spin spinning={isLoading}>
          <div className={css.content}>
            <div className={css.header}>
              <div className={css.headerUserName}>
                <UserShortCard
                  user={comment.author}
                  onClick={goToUserDetail}
                />
                <div className={css.meta}>
                  <span className={css.metaIcon}>
                    <CalendarOutlined />
                  </span>
                  <span>{moment(comment.lastClaimDate).format(FULL_TIME_FORMAT)}</span>
                </div>
              </div>
              <div className={cn(css.metaLine, css.meta)}>
                <Link
                  className={cn(css.metaLineItem, 'clickable')}
                  to={getPath('postDetail', { postId: comment.postId })}
                >
                  {content.goToPost}
                </Link>
                {isDisplayedRemainingCommentsButton ? (
                  <span
                    className={cn(css.metaLineItem, 'clickable')}
                    onClick={async () =>
                      await getComments({ commentId: comment.id, preloaderId: comment.id })
                    }
                  >
                    {content.show} {remainingComments} {content.answers}
                  </span>
                ) : null}
              </div>
            </div>
            <div className={css.body}>
              {comment.image && (
                <div>
                  <img
                    onClick={() => openModalHandler(comment.image)}
                    className={css.image}
                    src={comment?.image?.url}
                    alt=''
                  />
                </div>
              )}
              <div>
                <span>{renderCommentText(comment)}</span>
              </div>
            </div>
            {isDisplayedSpamPanel && (
              <div className={css.footer}>
                <SpamPanel
                  mode='comment'
                  item={comment}
                  isBlocking={!!isBlocking}
                  isUnblocking={!!isUnblocking}
                  onBlock={blockComment}
                  onUnblock={unblockComment}
                />
              </div>
            )}
          </div>
        </Spin>
        {!!store && store.list.length > 0 && (
          <div className={css.subCommentContainer}>
            {store.list.map((subComment) => (
              <SpamCommentCard
                key={subComment.id}
                isSubComment
                comment={subComment}
              />
            ))}
          </div>
        )}
      </div>
      {isVisible ? (
        <ImageModal
          onClose={closeModal}
          files={[
            {
              fileType: FileType.Image,
              image: currentImage,
            },
          ]}
        />
      ) : null}
    </>
  );
};

export default SpamCommentCard;
